import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "urql";
import { Link, useSearchParams } from "wouter";
import stableStringify from "json-stable-stringify";
import { Check } from "lucide-react";

import { Alert, AlertVariant } from "../../../components/src/Alert";
import { Dropdown } from "../../../components/src/Dropdown";
import { Form, Formik } from "formik";
import { cloneDeep, sortBy } from "lodash";
import { Button } from "../../../components/src/Button";
import { SearchListField, SubmitButton } from "../../../components/src/form";

import { Ticket, TicketTypeEnum, TicketStatusEnum, TicketFilterInput, TicketInput, PermissionAssetEnum, Me } from "../types.generated";
import { GET_CLIENTS_USERS, GET_TICKETS, UPSERT_TICKETS } from "../../../graphql/src/ticket/queries";
import { ReactDataGrid } from "../../../components/src/ReactDataGrid";
import { ICommandBarMenuItem } from "../../../components/src/common/CommandBar";
import {
    SelectColumnFilter,
    SelectColumnFilterArrayOfIds,
    SelectColumnFilterType
} from "../../../components/src/react-table/ReactTableFilters";
import { YesNoModal } from "../../../components/src/YesNoModal";

const ALL_ASSIGNEE_ID = "All";

interface FilterType {
    clientId: string;
    status: string;
    assigneeId: string;
    creatorId: string;
}

export const TicketsPage = (): React.ReactElement => {
    const [queryArgs, setQueryArgs] = useSearchParams();
    const [showFilter, setShowFilter] = useState(false);

    const [filter, setFilter] = useState<FilterType>({
        clientId: null,
        status: null,
        assigneeId: null,
        creatorId: null
    });

    const [modal, setModal] = useState<{ showModal: boolean; payload: TicketInput | TicketInput[] | null }>({
        showModal: false,
        payload: null
    });

    const [selectedTickets, setSelectedTickets] = useState<any[]>([]);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    const [{ fetching: loadingClientsUsers, error: errorClientsUsers, data }] = useQuery<{
        me?: Partial<Me>;
        ticketUsers?: Array<{ _id: string; name: string; clients?: Array<{ _id: string; name: string }> }>;
    }>({ query: GET_CLIENTS_USERS });

    const [{ fetching, error, data: dataTickets }] = useQuery<{ tickets?: Partial<Ticket>[] }>({
        query: GET_TICKETS,
        variables: {
            filter: {
                assigneeIdIn: filter.assigneeId && filter.assigneeId !== ALL_ASSIGNEE_ID ? [filter.assigneeId] : null,
                clientIds: filter.clientId ? [filter.clientId] : null,
                creatorIdIn: filter.creatorId ? [filter.creatorId] : null,
                statusIn: filter.status ? [filter.status as TicketStatusEnum] : null
            }
        }
    });

    const [_, upsertTickets] = useMutation(UPSERT_TICKETS);

    useEffect(() => {
        const assigneeId = data && data.me && data.me._id ? data.me._id : null;
        const ticketFilter: Partial<TicketFilterInput> = { statusIn: [TicketStatusEnum.Open] };
        if (assigneeId) ticketFilter.assigneeIdIn = [assigneeId];

        if (queryArgs.size === 0 && assigneeId) {
            setFilter({ clientId: null, status: TicketStatusEnum.Open, assigneeId: assigneeId, creatorId: null });
            setQueryArgs((prev) => {
                prev.set("status", TicketStatusEnum.Open);
                prev.set("assigneeId", assigneeId);
                return prev;
            });
            setShowFilter(true);
        }

        const newFilter: FilterType = { clientId: null, status: null, assigneeId: null, creatorId: null };
        const newTicketFilter: Partial<TicketFilterInput> = {};

        if (queryArgs.get("clientId")) {
            newFilter["clientId"] = queryArgs.get("clientId");
            newTicketFilter.clientIds = [queryArgs.get("clientId")];
        }

        if (queryArgs.get("assigneeId")) {
            const assigneeId = queryArgs.get("assigneeId");
            newFilter["assigneeId"] = assigneeId;
            if (assigneeId !== ALL_ASSIGNEE_ID) {
                newTicketFilter.assigneeIdIn = [assigneeId];
            }
        }

        if (queryArgs.get("status")) {
            newFilter["status"] = queryArgs.get("status");
            newTicketFilter.statusIn = [queryArgs.get("status") as TicketStatusEnum];
        }

        if (queryArgs.get("creatorId")) {
            newFilter["creatorId"] = queryArgs.get("creatorId");
            newTicketFilter.creatorIdIn = [queryArgs.get("creatorId")];
        }

        if (stableStringify(filter) !== stableStringify(newFilter)) {
            setFilter(newFilter);
            setShowFilter(true);
        }
    }, [data, filter, queryArgs, setQueryArgs]);

    const handleSelectedChange = (items: any[]) => {
        setSelectedTickets(items);
    };

    const commands = useMemo<ICommandBarMenuItem[]>(() => {
        const openTickets = selectedTickets.filter((ticket) => ticket.state && ticket.state.status === TicketStatusEnum.Open);
        if (openTickets.length === 0) {
            return [];
        }

        return [
            {
                itemKey: "closeTickets",
                text: "Close tickets",
                icon: <Check />,
                onClick: () => {
                    const payload: TicketInput[] = openTickets.map((ticket) => ({
                        _id: ticket._id,
                        title: ticket.title,
                        clientId: ticket.clientId,
                        state: {
                            status: TicketStatusEnum.Closed,
                            assigneeIds: ticket.state.assigneeIds,
                            comment: ""
                        },
                        type: ticket.type
                    }));
                    setModal({ showModal: true, payload });
                }
            }
        ];
    }, [selectedTickets]);

    const columns = useMemo(() => {
        const assigneesById: Record<string, { _id: string; name: string }> = {};

        if (dataTickets && dataTickets.tickets) {
            dataTickets.tickets.forEach((ticket) => {
                if (ticket.state && ticket.state.assigneeIds) {
                    ticket.state.assignees.forEach((assignee) => {
                        if (!(assignee._id in assigneesById)) {
                            assigneesById[assignee._id] = assignee;
                        }
                    });
                }
            });
        }

        return [
            {
                header: "_id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/tickets/${row.original._id}`}>{cellProps.getValue()}</Link>;
                },
                size: 200
            },
            {
                header: "Client(Owner)",
                id: "client.name",
                accessorKey: "client.name",
                size: 200,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.client) {
                        return <Link to={`/parties/${row.original.client._id}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilter
            },
            {
                header: "Creator",
                id: "creator.name",
                accessorKey: "creator.name",
                size: 200,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.creator) {
                        return <Link to={`/admin/users/${row.original.creatorId}`}>{cellProps.getValue()}</Link>;
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilter
            },
            {
                header: "Assignees",
                id: "state.assigneeIds",
                accessorKey: "state.assigneeIds",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (value) {
                        return (
                            <Fragment>
                                {value.map((id) => {
                                    return (
                                        <Link key={id} to={`/admin/users/${id}`}>
                                            {assigneesById[id].name}
                                        </Link>
                                    );
                                })}
                            </Fragment>
                        );
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilterArrayOfIds(Object.values(assigneesById)),
                size: 200
            },
            {
                header: "Types",
                accessorKey: "type",
                filter: SelectColumnFilterType(TicketTypeEnum)
            },
            {
                header: "Title",
                accessorKey: "title",
                size: 200
            },
            {
                header: "Status",
                id: "state.status",
                accessorKey: "state.status",
                filter: SelectColumnFilterType(TicketStatusEnum),
                size: 75,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    const value = cellProps.getValue();
                    if (value !== "Open") {
                        return <div>{value}</div>;
                    }
                    return (
                        <Dropdown>
                            <Dropdown.Toggle as="a" bsPrefix="m-dropdown__toggle" id={row.original._id}>
                                {value}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item
                                    key={row.original._id}
                                    onClick={async () => {
                                        const payload: TicketInput = {
                                            _id: row.original._id,
                                            title: row.original.title,
                                            clientId: row.original.clientId,
                                            state: {
                                                status: TicketStatusEnum.Closed,
                                                assigneeIds: row.original.state.assigneeIds,
                                                comment: ""
                                            },
                                            type: row.original.type
                                        };
                                        setModal({ showModal: true, payload });
                                    }}
                                >
                                    Close ticket
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    );
                }
            },
            {
                header: "Created",
                accessorKey: "createTimestamp",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (value) {
                        return value.substring(0, 10);
                    } else {
                        return null;
                    }
                },
                filter: SelectColumnFilter,
                size: 90
            }
        ];
    }, [dataTickets]);

    if (fetching || loadingClientsUsers) return <div>Loading...</div>;
    if (error)
        return (
            <div>
                <p>error tickets:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );
    if (errorClientsUsers)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(errorClientsUsers, null, 2)}</pre>
            </div>
        );

    const users: { _id: string; name: string }[] = [];
    const statuses: { _id: string; name: string }[] = [];
    const clientsById: Record<string, { _id: string; name: string }> = {};

    data.ticketUsers.forEach((user) => {
        users.push({ _id: user._id, name: user.name });
        user.clients.forEach((client) => {
            if (client && !clientsById[client._id]) {
                clientsById[client._id] = { _id: client._id, name: client.name };
            }
        });
    });
    Object.values(TicketStatusEnum).forEach((status) => {
        statuses.push({ _id: status, name: status });
    });

    const toggleFilter = () => {
        setShowFilter(!showFilter);
    };

    const getModalWarningText = () => {
        if (Array.isArray(modal.payload)) {
            const count = modal.payload.length;
            return `Are you sure you want to close ${count} ticket${count > 1 ? "s" : ""}?`;
        } else if (modal.payload) {
            return `Are you sure you want to close the ticket with id ${modal.payload._id}?`;
        }
        return "";
    };

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={getModalWarningText()}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    yesText={Array.isArray(modal.payload) ? "Close tickets" : "Close ticket"}
                    onYes={async () => {
                        const input = Array.isArray(modal.payload) ? modal.payload : [modal.payload];
                        await upsertTickets({ input })
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                } else {
                                    setSelectedTickets([]);
                                }
                                return true;
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            });
                    }}
                />
            ) : null}

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    {alert.visible ? (
                        <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                            {alert.message}
                        </Alert>
                    ) : null}
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                {data &&
                data.me &&
                data.me.frontendRole &&
                data.me.frontendRole.assets &&
                (data.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) || data.me.frontendRole.assets.length === 0) ? (
                    <div className="w-full mb-3 text-right">
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manuals
                        </a>
                    </div>
                ) : null}
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">Number of tickets: {dataTickets.tickets.length}</div>
                <div className="w-full px-2 mb-4 text-right">
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => {
                            const win = window.open("/tickets/new", "_self");
                            win.focus();
                        }}
                    >
                        New ticket
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4 pl-0">
                    {showFilter ? (
                        <Button variant="ghost" className="underline text-sky-600 hover:text-sky-800 border-0 p-0" onClick={toggleFilter}>
                            Hide filter
                        </Button>
                    ) : (
                        <Button variant="ghost" className="underline text-sky-600 hover:text-sky-800 border-0 p-0" onClick={toggleFilter}>
                            Show filter
                        </Button>
                    )}
                </div>
            </div>
            {showFilter ? (
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full px-2 mb-4">
                        <Formik
                            enableReinitialize={true}
                            initialValues={filter}
                            validate={() => {
                                const errors: any = {};
                                return Object.keys(errors).length > 0 ? errors : {};
                            }}
                            onSubmit={async (submitValues) => {
                                const newValues: Partial<TicketFilterInput> = {};

                                if (submitValues.clientId) {
                                    newValues.clientIds = [cloneDeep(submitValues.clientId)];
                                }
                                if (submitValues.assigneeId && submitValues.assigneeId !== ALL_ASSIGNEE_ID) {
                                    newValues.assigneeIdIn = [cloneDeep(submitValues.assigneeId)];
                                }
                                if (submitValues.creatorId) {
                                    newValues.creatorIdIn = [cloneDeep(submitValues.creatorId)];
                                }
                                if (submitValues.status) {
                                    newValues.statusIn = [cloneDeep(submitValues.status) as TicketStatusEnum];
                                }

                                setQueryArgs((prev) => {
                                    if (submitValues.clientId) {
                                        prev.set("clientId", submitValues.clientId);
                                    }
                                    if (submitValues.assigneeId) {
                                        prev.set("assigneeId", submitValues.assigneeId);
                                    } else {
                                        prev.delete("assigneeId");
                                    }
                                    if (submitValues.creatorId) {
                                        prev.set("creatorId", submitValues.creatorId);
                                    }
                                    if (submitValues.status) {
                                        prev.set("status", submitValues.status);
                                    }
                                    return prev;
                                });
                            }}
                        >
                            {({ isSubmitting }) => {
                                return (
                                    <Form autoComplete="off">
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-3/12 px-2 mb-4">
                                                <SearchListField
                                                    name="clientId"
                                                    label="Client"
                                                    className=""
                                                    disabled={isSubmitting}
                                                    options={sortBy(Object.values(clientsById), "name")}
                                                />
                                            </div>
                                            <div className="w-full md:w-3/12 px-2 mb-4">
                                                <SearchListField
                                                    name="assigneeId"
                                                    label={"Assignee"}
                                                    className=""
                                                    disabled={isSubmitting}
                                                    options={[{ _id: ALL_ASSIGNEE_ID, name: "All" }, ...sortBy(users, "name")]}
                                                />
                                            </div>
                                            <div className="w-full md:w-3/12 px-2 mb-4">
                                                <SearchListField
                                                    name="creatorId"
                                                    label={"Creator"}
                                                    className=""
                                                    disabled={isSubmitting}
                                                    options={sortBy(users, "name")}
                                                />
                                            </div>
                                            <div className="w-full md:w-3/12 px-2 mb-4">
                                                <SearchListField
                                                    name="status"
                                                    label={"Status"}
                                                    className=""
                                                    disabled={isSubmitting}
                                                    options={sortBy(statuses, "name")}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap -mx-2 mb-4">
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <div className="flex flex-wrap -mx-2 mt-4">
                                                    <div className="w-full px-2 mb-4">
                                                        <SubmitButton disabled={fetching} label={"Apply"} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>
            ) : null}

            <div className="flex flex-wrap -mx-2">
                <Fragment>
                    <div className="w-full px-2 mb-4">
                        <ReactDataGrid
                            columns={columns}
                            data={
                                dataTickets.tickets
                                    ? [...dataTickets.tickets].sort(function (x, y) {
                                          return new Date(y.createTimestamp).getTime() - new Date(x.createTimestamp).getTime();
                                      })
                                    : []
                            }
                            defaultHiddenColumns={[]}
                            commands={commands}
                            onSelectedChange={handleSelectedChange}
                        />
                    </div>
                </Fragment>
            </div>
        </div>
    );
};
